// @erp/module-cms/schemas — the PUBLICATION TITLES content type wire contracts (db-cms
// 0022; decided with the owner 2026-09-14/20, ADR-010 §2.11). A TITLE is the periodical
// or series a publication belongs to — a JOURNAL, a MAGAZINE, a NEWSLETTER, a BOOK SERIES
// or a REPORT SERIES — with its own page: the "about this title" copy, the editorial
// board, the ISSN and frequency, a logo and a cover, outbound links, and an archive of
// the publications that link to it by id (cms_publication.title_id). Browser-safe: zod +
// plain data only (the admin form and the gateway parse the SAME schemas).
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ ONE ROW PER LOCALE like every content type (the 0002 law): a translation is a  │
// │ SIBLING ROW under a different `locale`, the same slug. An issue links to the   │
// │ title of ITS OWN locale (the migration links the Hindi issues to the Hindi     │
// │ title); the admin picker offers the row's locale's titles only.                │
// ├──────────────────────────────────────────────────────────────────────────────┤
// │ SECURITY — body_html / notes_public_html are stored RAW and sanitised ON THE   │
// │ WAY OUT (api/sanitize.ts, every /public/* route). `notesPrivate` is ADMIN ONLY: │
// │ it is on the admin DTO and on NO public shape (schemas/public.ts omits it; the  │
// │ feed tests grep every public DTO for it). A private title (0011) shows locked   │
// │ metadata on the public read and unlocks with a PIN like a publication.         │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";
import { SLUG_MAX, SLUG_RE } from "./categories.js";
import { Locale } from "./locale.js";
import { BODY_HTML_MAX, SUMMARY_MAX } from "./announcements.js";
import { HttpUrl } from "./href.js";
import { PinField, RecordAccessLevel } from "./private.js";
import { PublishingStatus } from "./settings.js";
import { Contributor, Contributors, Highlight, Highlights, Tags } from "./structured.js";

// ── The kind vocabulary — which FAMILY a title is. An issue (docType journal_issue)
// links to a PERIODICAL kind; a book-in-a-series links to a SERIES kind. ─────────────
export const PUBLICATION_TITLE_KINDS = ["journal", "magazine", "newsletter", "book_series", "report_series"] as const;
export const PublicationTitleKind = z.enum(PUBLICATION_TITLE_KINDS);
export type PublicationTitleKind = z.infer<typeof PublicationTitleKind>;

/** The kinds an ISSUE may belong to (the issue form's picker filters on these). */
export const PERIODICAL_KINDS: readonly PublicationTitleKind[] = ["journal", "magazine", "newsletter"];
/** The kinds a BOOK or document may belong to. */
export const SERIES_KINDS: readonly PublicationTitleKind[] = ["book_series", "report_series"];

// ── Frequency — an ENUM here (the issue's doc_meta.frequency is a display LABEL; the
// form derives that label from this value: frequencyLabel below). ────────────────────
export const PUBLICATION_FREQUENCIES = ["annual", "half_yearly", "quarterly", "monthly", "irregular"] as const;
export const PublicationFrequency = z.enum(PUBLICATION_FREQUENCIES);
export type PublicationFrequency = z.infer<typeof PublicationFrequency>;

/** The display label the issue form writes into doc_meta.frequency when a title is picked. */
export const FREQUENCY_LABELS: Readonly<Record<PublicationFrequency, string>> = {
  annual: "Annual",
  half_yearly: "Half-yearly",
  quarterly: "Quarterly",
  monthly: "Monthly",
  irregular: "Irregular",
};
export const frequencyLabel = (f: PublicationFrequency | null | undefined): string => (f ? FREQUENCY_LABELS[f] : "");

// ── The editorial RUN state — active | ceased. A ceased title keeps its archive and its
// page; it is orthogonal to the publishing `status` (draft | published | archived). ──────
export const PUBLICATION_TITLE_RUN_STATUSES = ["active", "ceased"] as const;
export const PublicationTitleRunStatus = z.enum(PUBLICATION_TITLE_RUN_STATUSES);
export type PublicationTitleRunStatus = z.infer<typeof PublicationTitleRunStatus>;

/** The publishing status — REUSED from settings (the announcements/publications precedent). */
export const PublicationTitleStatus = PublishingStatus;
export type PublicationTitleStatus = z.infer<typeof PublicationTitleStatus>;

// ── Links — the outbound references a title page offers, each with a KIND the site
// groups on: the title's own website, where it is indexed, how to subscribe, where to
// obtain a copy. Absolute http(s) only (the attachments rule — a static site served from
// another host has no meaning for a relative link). ─────────────────────────────────────
export const PUBLICATION_LINK_KINDS = ["website", "indexing", "subscription", "obtain"] as const;
export const PublicationLinkKind = z.enum(PUBLICATION_LINK_KINDS);
export type PublicationLinkKind = z.infer<typeof PublicationLinkKind>;

export const PublicationLink = z.object({
  label: z.string().trim().min(1).max(200),
  url: HttpUrl,
  kind: PublicationLinkKind,
});
export type PublicationLink = z.infer<typeof PublicationLink>;
/** Per-row cap — a title page lists a handful of outbound references. */
export const PUBLICATION_LINKS_MAX = 20;
export const PublicationLinks = z.array(PublicationLink).max(PUBLICATION_LINKS_MAX);
export type PublicationLinks = z.infer<typeof PublicationLinks>;

/** The longest ISSN accepted — "2277-2456" with or without the hyphen; stored as typed, shown verbatim (the issue's rule). */
export const ISSN_MAX = 20;
/** A founded year — a plausible four-digit year; the far bound leaves room, the near bound refuses a typo like 20226. */
export const FoundedYear = z.number().int().min(1000).max(2999);

// ── The write-side field validators (one source, Create/Update both derive) ─────────
const titleCore = {
  name: z.string().trim().min(1).max(300),
  /** The FORMAT rule only — the site's reserved section names (./reserved-slugs.ts, shared with publications) are judged by the service on the EFFECTIVE slug, explicit or derived → 422 CMS_SLUG_RESERVED. */
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  kind: PublicationTitleKind,
  summary: z.string().trim().max(SUMMARY_MAX).nullable(),
  /** RAW HTML ("about this title") — sanitised ONLY on the public read, never here. Nullable to clear. */
  bodyHtml: z.string().max(BODY_HTML_MAX).nullable(),
  /** cms_media id of the masthead / logo (the 0003 presign flow). Nullable to clear. */
  logoMediaId: EntityId.nullable(),
  /** cms_media id of the representative cover. Nullable to clear. */
  coverMediaId: EntityId.nullable(),
  frequency: PublicationFrequency.nullable(),
  issn: z.string().trim().max(ISSN_MAX).nullable(),
  publisher: z.string().trim().max(300).nullable(),
  foundedYear: FoundedYear.nullable(),
  runStatus: PublicationTitleRunStatus,
  /** The editorial board — the 0018 Contributor shape (≤100). Nullable to clear. */
  contributors: Contributors.nullable(),
  links: PublicationLinks.nullable(),
  /** A second RAW HTML block (submissions, indexing, subscription) — sanitised on read. Nullable to clear. */
  notesPublicHtml: z.string().max(BODY_HTML_MAX).nullable(),
  /** ADMIN ONLY — never on a public DTO. Plain text. Nullable to clear. */
  notesPrivate: z.string().max(BODY_HTML_MAX).nullable(),
  highlights: Highlights.nullable(),
  tags: Tags.nullable(),
  status: PublicationTitleStatus,
  /** Explicit publish instant (epoch-ms); usually left unset — the service stamps it on the publish flip. Nullable to clear. */
  publishedAt: EpochMs.nullable(),
  /** The row's locale — a canonical BCP 47 tag (./locale.ts). */
  locale: Locale,
};

/**
 * Create body — name + kind required; slug OPTIONAL (the service derives it via
 * slugify(name), id-fallback on a non-sluggable name). status / locale / runStatus carry the
 * DDL defaults; published_at is normally derived from the status. No companion
 * announcement: a title is a container, not a notice (decisions.md 2026-09-20).
 */
export const CreateCmsPublicationTitle = z.object({
  name: titleCore.name,
  slug: titleCore.slug.optional(),
  kind: titleCore.kind,
  summary: titleCore.summary.optional(),
  bodyHtml: titleCore.bodyHtml.optional(),
  logoMediaId: titleCore.logoMediaId.optional(),
  coverMediaId: titleCore.coverMediaId.optional(),
  frequency: titleCore.frequency.optional(),
  issn: titleCore.issn.optional(),
  publisher: titleCore.publisher.optional(),
  foundedYear: titleCore.foundedYear.optional(),
  runStatus: titleCore.runStatus.default("active"),
  contributors: titleCore.contributors.optional(),
  links: titleCore.links.optional(),
  notesPublicHtml: titleCore.notesPublicHtml.optional(),
  notesPrivate: titleCore.notesPrivate.optional(),
  highlights: titleCore.highlights.optional(),
  tags: titleCore.tags.optional(),
  status: titleCore.status.default("draft"),
  publishedAt: titleCore.publishedAt.optional(),
  locale: titleCore.locale.default("en"),
  // Private-records slice (0011): access_level + the WRITE-ONLY pin (see private.ts).
  accessLevel: RecordAccessLevel.default("public"),
  pin: PinField.optional(),
});
export type CreateCmsPublicationTitle = z.infer<typeof CreateCmsPublicationTitle>;

/** Update body — a partial PATCH over the same fields (slug stays STABLE on rename; a slug collision still maps to 409). */
export const UpdateCmsPublicationTitle = z
  .object({
    name: titleCore.name,
    slug: titleCore.slug,
    kind: titleCore.kind,
    summary: titleCore.summary,
    bodyHtml: titleCore.bodyHtml,
    logoMediaId: titleCore.logoMediaId,
    coverMediaId: titleCore.coverMediaId,
    frequency: titleCore.frequency,
    issn: titleCore.issn,
    publisher: titleCore.publisher,
    foundedYear: titleCore.foundedYear,
    runStatus: titleCore.runStatus,
    contributors: titleCore.contributors,
    links: titleCore.links,
    notesPublicHtml: titleCore.notesPublicHtml,
    notesPrivate: titleCore.notesPrivate,
    highlights: titleCore.highlights,
    tags: titleCore.tags,
    status: titleCore.status,
    publishedAt: titleCore.publishedAt,
    locale: titleCore.locale,
    accessLevel: RecordAccessLevel,
    pin: PinField,
  })
  .partial();
export type UpdateCmsPublicationTitle = z.infer<typeof UpdateCmsPublicationTitle>;

/** List query — keyset cursor+limit + optional ?kind ?status ?runStatus ?locale ?q filters (soft-deleted rows excluded unless ?trashed). */
export const CmsPublicationTitleListQuery = CursorQuery.extend({
  kind: PublicationTitleKind.optional(),
  status: PublicationTitleStatus.optional(),
  runStatus: PublicationTitleRunStatus.optional(),
  /** Exact-eq on the row's locale; omitted lists every locale. */
  locale: Locale.optional(),
  /** A bounded name search (the picker's type-ahead) — a LIKE over `name`, the media-list LIKE hygiene. */
  q: z.string().trim().max(100).optional(),
  /** Opt-in TRASH view — the publications rule: only "1"/"true" opt in; anything else heals to live. */
  trashed: z
    .enum(["1", "true"])
    .transform(() => true)
    .optional()
    .catch(undefined),
});
export type CmsPublicationTitleListQuery = z.infer<typeof CmsPublicationTitleListQuery>;

/**
 * The ADMIN wire DTO (camelCase; audit actor columns stay server-side). Carries
 * `notesPrivate` — the ONE field the public shapes omit (schemas/public.ts). `deletedAt`
 * surfaces so the trash view tells live rows from trashed ones.
 */
export const CmsPublicationTitle = z.object({
  id: EntityId,
  slug: z.string(),
  locale: z.string(),
  kind: PublicationTitleKind,
  name: z.string(),
  summary: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  logoMediaId: EntityId.nullable(),
  coverMediaId: EntityId.nullable(),
  frequency: PublicationFrequency.nullable(),
  issn: z.string().nullable(),
  publisher: z.string().nullable(),
  foundedYear: z.number().int().nullable(),
  runStatus: PublicationTitleRunStatus,
  /** The parsed editorial board ([] when none). */
  contributors: z.array(Contributor),
  /** The parsed links ([] when none). */
  links: z.array(PublicationLink),
  notesPublicHtml: z.string().nullable(),
  /** ADMIN ONLY. */
  notesPrivate: z.string().nullable(),
  highlights: z.array(Highlight),
  tags: z.array(z.string()),
  status: PublicationTitleStatus,
  publishedAt: EpochMs.nullable(),
  accessLevel: RecordAccessLevel,
  isLocked: z.boolean(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  deletedAt: EpochMs.nullable(),
});
export type CmsPublicationTitle = z.infer<typeof CmsPublicationTitle>;
